import type { CoverageSchemaResponse } from '@/features/quotes/types/group-life/coverage-schema.types';
import type { CoverageSelection } from '@/features/quotes/utils/group-life/coverage-resolver';

function proportionOverrideFromSelection(
  sel: CoverageSelection,
): number | undefined {
  if (
    sel.kind === 'PROPORTIONAL_TO_RISK' ||
    sel.kind === 'PROPORTIONAL_TO_COVERAGE'
  ) {
    return sel.proportionOverride;
  }
  return undefined;
}

/** Corrige seleções hidratadas com `kind` errado (ex.: DECLARED vindo do back
 *  sem `SumInsuredDefinition` para coberturas proporcionais do schema). */
export function coerceCoverageSelectionToSchema(
  sel: CoverageSelection,
  def: CoverageSchemaResponse['coverages'][number],
  options?: { uniformCapitalReais?: number },
): CoverageSelection {
  if (!sel.enabled) return sel;

  const inferredProportionFromDeclared =
    sel.kind === 'DECLARED' &&
    sel.capital != null &&
    sel.capital > 0 &&
    options?.uniformCapitalReais != null &&
    options.uniformCapitalReais > 0 &&
    def.sumInsuredDefinition === 'PROPORTIONAL_TO_RISK'
      ? // Proporção com até 2 casas decimais (mesma precisão do campo de %).
        Math.round((sel.capital / options.uniformCapitalReais) * 10000) / 100
      : undefined;

  if (sel.kind === def.sumInsuredDefinition) {
    if (
      def.sumInsuredDefinition === 'PROPORTIONAL_TO_RISK' &&
      sel.kind === 'PROPORTIONAL_TO_RISK' &&
      sel.proportionOverride == null &&
      inferredProportionFromDeclared != null
    ) {
      return { ...sel, proportionOverride: inferredProportionFromDeclared };
    }
    return sel;
  }

  const proportionOverride =
    proportionOverrideFromSelection(sel) ?? inferredProportionFromDeclared;

  switch (def.sumInsuredDefinition) {
    case 'DECLARED':
      return {
        kind: 'DECLARED',
        enabled: true,
        capital:
          sel.kind === 'DECLARED'
            ? sel.capital
            : null,
      };
    case 'PROPORTIONAL_TO_COVERAGE':
      return {
        kind: 'PROPORTIONAL_TO_COVERAGE',
        enabled: true,
        proportionOverride: proportionOverride ?? def.proportion,
      };
    case 'PROPORTIONAL_TO_RISK':
      return {
        kind: 'PROPORTIONAL_TO_RISK',
        enabled: true,
        proportionOverride: proportionOverride ?? def.proportion,
      };
    case 'BENEFIT_SELECTION':
      return {
        kind: 'BENEFIT_SELECTION',
        enabled: true,
        selectedChildCoverageCode:
          sel.kind === 'BENEFIT_SELECTION'
            ? sel.selectedChildCoverageCode
            : null,
      };
    case 'BENEFIT_SELECTION_WITH_SI':
      return {
        kind: 'BENEFIT_SELECTION_WITH_SI',
        enabled: true,
        selectedChildCoverageCode:
          sel.kind === 'BENEFIT_SELECTION' ||
          sel.kind === 'BENEFIT_SELECTION_WITH_SI'
            ? sel.selectedChildCoverageCode
            : null,
        capital:
          sel.kind === 'BENEFIT_SELECTION_WITH_SI'
            ? sel.capital
            : sel.kind === 'DECLARED'
              ? sel.capital
              : null,
      };
    case 'EXCLUSIVE_GROUP':
      return { kind: 'EXCLUSIVE_GROUP', enabled: true };
    default:
      return sel;
  }
}

/** Capital (SI) que uma seleção-filho pode carregar (usado ao reconstruir
 *  BENEFIT_SELECTION_WITH_SI a partir do código do filho persistido). */
function selectionCapital(sel: CoverageSelection | undefined): number | null {
  if (!sel) return null;
  if (sel.kind === 'DECLARED' || sel.kind === 'BENEFIT_SELECTION_WITH_SI') {
    return sel.capital;
  }
  return null;
}

export function normalizeHydratedCoverageSelections(
  schema: CoverageSchemaResponse,
  coverages: Record<string, CoverageSelection>,
  options?: { uniformCapitalReais?: number },
): Record<string, CoverageSelection> {
  let changed = false;
  const next: Record<string, CoverageSelection> = { ...coverages };

  for (const def of schema.coverages) {
    // BENEFIT_SELECTION / BENEFIT_SELECTION_WITH_SI: o back persiste a opção
    // escolhida pelo CÓDIGO DO FILHO (a opção É a cobertura que vive na
    // apólice). Ao reabrir, o mapa hidratado traz a entrada keyed pelo filho —
    // não pelo pai. Reconstruímos a seleção do pai a partir do filho presente,
    // para o dropdown voltar preenchido.
    if (
      def.sumInsuredDefinition === 'BENEFIT_SELECTION' ||
      def.sumInsuredDefinition === 'BENEFIT_SELECTION_WITH_SI'
    ) {
      const parentSel = coverages[def.code];
      const parentHasOption =
        (parentSel?.kind === 'BENEFIT_SELECTION' ||
          parentSel?.kind === 'BENEFIT_SELECTION_WITH_SI') &&
        parentSel.enabled &&
        !!parentSel.selectedChildCoverageCode;

      if (!parentHasOption) {
        const childEntry = def.children.find((c) => coverages[c.code]?.enabled);
        if (childEntry) {
          changed = true;
          next[def.code] =
            def.sumInsuredDefinition === 'BENEFIT_SELECTION_WITH_SI'
              ? {
                  kind: 'BENEFIT_SELECTION_WITH_SI',
                  enabled: true,
                  selectedChildCoverageCode: childEntry.code,
                  capital: selectionCapital(coverages[childEntry.code]),
                }
              : {
                  kind: 'BENEFIT_SELECTION',
                  enabled: true,
                  selectedChildCoverageCode: childEntry.code,
                };
          // Remove a entrada órfã keyed pelo filho para não re-persistir/duplicar.
          delete next[childEntry.code];
          continue;
        }
      }
    }

    const sel = coverages[def.code];
    if (!sel?.enabled) continue;
    const coerced = coerceCoverageSelectionToSchema(sel, def, options);
    if (coerced.kind !== sel.kind || coerced !== sel) {
      changed = true;
      next[def.code] = coerced;
    }
  }

  return changed ? next : coverages;
}
